import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import type { LoadOptions } from "react-select-async-paginate";
import { useDispatch } from "react-redux";
import { Plus, X } from "lucide-react";
import toast from "react-hot-toast";
import { RootDispatch } from "../../../../store";
import {
  AvailableTransferMaterial,
  CreateMaterialTransferPayload,
  MaterialTransferItemInput,
} from "../../../../models/materialStockTransfer.model";
import { fetchAvailableTransferMaterials } from "../../../../store/slices/StockTransfer/materialStockTransferSlice";
import {
  convertMaterialQuantity,
  getMaterialDisplayUnit,
  validateMaterialTransferQuantity,
} from "./materialTransfer.utils";
import MaterialTransferItemEditor from "./MaterialTransferItemEditor";

interface WarehouseOption {
  value: string;
  label: string;
}

interface MaterialOption {
  value: string;
  label: string;
  material: AvailableTransferMaterial;
}

interface QueuedMaterial {
  material: AvailableTransferMaterial;
  quantity: number;
  displayQuantity: number;
  displayUnit: string;
  serialNumbers: string[];
  senderRemark?: string;
}

interface CreateMaterialTransferSectionProps {
  warehouseOptions: WarehouseOption[];
  isAdmin: boolean;
  warehouseAssigned: string | null;
  materialsRefreshKey: number;
  loading: boolean;
  onSubmit: (payload: CreateMaterialTransferPayload) => Promise<boolean>;
}

const CreateMaterialTransferSection: React.FC<
  CreateMaterialTransferSectionProps
> = ({
  warehouseOptions,
  isAdmin,
  warehouseAssigned,
  materialsRefreshKey,
  loading,
  onSubmit,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceWarehouse, setSourceWarehouse] =
    useState<WarehouseOption | null>(null);
  const [destinationWarehouse, setDestinationWarehouse] =
    useState<WarehouseOption | null>(null);
  const [selectedMaterial, setSelectedMaterial] =
    useState<MaterialOption | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [serialNumbers, setSerialNumbers] = useState("");
  const [senderRemark, setSenderRemark] = useState("");
  const [queuedMaterials, setQueuedMaterials] = useState<QueuedMaterial[]>([]);

  const sourceWarehouseId = isAdmin
    ? sourceWarehouse?.value
    : warehouseAssigned || undefined;

  useEffect(() => {
    if (!isAdmin || !warehouseAssigned) return;
    const assignedOption = warehouseOptions.find(
      (warehouse) => warehouse.value === warehouseAssigned,
    );
    if (assignedOption) {
      setSourceWarehouse((current) => current || assignedOption);
    }
  }, [isAdmin, warehouseAssigned, warehouseOptions]);

  const loadMaterialOptions: LoadOptions<
    MaterialOption,
    any,
    { page: number }
  > = async (search, _loadedOptions, additional) => {
    const page = additional?.page || 1;
    if (!sourceWarehouseId) {
      return { options: [], hasMore: false, additional: { page } };
    }

    try {
      const response = await dispatch(
        fetchAvailableTransferMaterials({
          page,
          limit: 10,
          search: search.trim(),
          ...(isAdmin ? { warehouseId: sourceWarehouseId } : {}),
        }),
      ).unwrap();

      return {
        options: response.materials.map((material) => ({
          value: material.materialId,
          label: `${material.name} · ${material.sku}`,
          material,
        })),
        hasMore: page < (response.pagination?.totalPages || 1),
        additional: { page: page + 1 },
      };
    } catch {
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  const resetEntry = () => {
    setSelectedMaterial(null);
    setQuantity("1");
    setSerialNumbers("");
    setSenderRemark("");
  };

  const addMaterial = () => {
    if (!selectedMaterial) {
      toast.error("Select a material first");
      return;
    }
    if (
      queuedMaterials.some(
        (item) => item.material.materialId === selectedMaterial.material.materialId,
      )
    ) {
      toast.error("This material is already in the transfer list");
      return;
    }

    const material = selectedMaterial.material;
    const validated = validateMaterialTransferQuantity(
      material,
      quantity,
      serialNumbers,
    );
    const validatedQuantity = validated.quantity;
    if (validated.error || validatedQuantity === undefined) {
      toast.error(validated.error || "Enter a valid quantity");
      return;
    }

    setQueuedMaterials((items) => [
      {
        material,
        quantity: validatedQuantity,
        displayQuantity: Number(quantity),
        displayUnit: getMaterialDisplayUnit(material.unit),
        serialNumbers: validated.serialNumbers || [],
        senderRemark: senderRemark.trim() || undefined,
      },
      ...items,
    ]);
    resetEntry();
  };

  const handleSubmit = async () => {
    if (!sourceWarehouseId) {
      toast.error(
        isAdmin
          ? "Select a source warehouse"
          : "No source warehouse is assigned to your account",
      );
      return;
    }
    if (!destinationWarehouse) {
      toast.error("Select a destination warehouse");
      return;
    }
    if (sourceWarehouseId === destinationWarehouse.value) {
      toast.error("Source and destination warehouses must be different");
      return;
    }
    if (queuedMaterials.length === 0) {
      toast.error("Add at least one material");
      return;
    }

    const items: MaterialTransferItemInput[] = queuedMaterials.map((item) => ({
      materialId: item.material.materialId,
      quantity: item.quantity,
      ...(item.material.requireSerialNumber
        ? { serialNumbers: item.serialNumbers }
        : {}),
      ...(item.senderRemark ? { senderRemark: item.senderRemark } : {}),
    }));
    const payload: CreateMaterialTransferPayload = {
      ...(isAdmin && sourceWarehouseId
        ? { fromWarehouse: sourceWarehouseId }
        : {}),
      toWarehouse: destinationWarehouse.value,
      items,
    };

    if (await onSubmit(payload)) {
      setQueuedMaterials([]);
      setDestinationWarehouse(null);
      resetEntry();
      setIsModalOpen(false);
    }
  };

  const availableDestinationWarehouses = warehouseOptions.filter(
    (warehouse) => warehouse.value !== sourceWarehouseId,
  );
  const sourceWarehouseLabel =
    warehouseOptions.find((warehouse) => warehouse.value === sourceWarehouseId)
      ?.label || (warehouseAssigned ? "Assigned warehouse" : "No warehouse assigned");

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#0077C0] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0069AA] focus:outline-none focus:ring-2 focus:ring-[#0077C0] focus:ring-offset-2 sm:w-auto"
      >
        <Plus size={19} />
        Start material transfer
      </button>

      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !loading) {
                setIsModalOpen(false);
              }
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-material-transfer-title"
              className="relative max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl bg-slate-100 p-3 shadow-2xl sm:rounded-xl sm:p-5"
            >
              <header className="sticky top-0 z-10 mb-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <h2
                  id="create-material-transfer-title"
                  className="text-base font-semibold text-gray-900 sm:text-lg"
                >
                  Create material transfer
                </h2>
                <button
                  type="button"
                  aria-label="Close material transfer dialog"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loading}
                  className="flex min-h-10 min-w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </header>

              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
                <div className="grid gap-4 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2 sm:p-4">
                  {isAdmin ? (
                    <label className="block text-xs font-medium text-gray-600">
                      From warehouse *
                      <Select
                        value={sourceWarehouse}
                        onChange={(option) => {
                          setSourceWarehouse(option);
                          if (option?.value === destinationWarehouse?.value) {
                            setDestinationWarehouse(null);
                          }
                        }}
                        options={warehouseOptions}
                        isDisabled={queuedMaterials.length > 0}
                        placeholder="Select source warehouse..."
                        className="mt-1 text-sm"
                        menuPortalTarget={document.body}
                        styles={{
                          control: (base) => ({ ...base, minHeight: 44 }),
                          menuPortal: (base) => ({ ...base, zIndex: 10010 }),
                        }}
                      />
                    </label>
                  ) : (
                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                      <p className="text-xs font-medium text-gray-500">
                        From warehouse
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-800">
                        {sourceWarehouseLabel}
                      </p>
                    </div>
                  )}

                  <label className="block text-xs font-medium text-gray-600">
                    To warehouse *
                    <Select
                      value={destinationWarehouse}
                      onChange={setDestinationWarehouse}
                      options={availableDestinationWarehouses}
                      placeholder="Select destination warehouse..."
                      className="mt-1 text-sm"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({ ...base, minHeight: 44 }),
                        menuPortal: (base) => ({ ...base, zIndex: 10010 }),
                      }}
                    />
                  </label>
                </div>

                <div className="mt-5">
                  <label className="mb-1 block text-sm font-semibold text-gray-800">
                    Search materials
                  </label>
                  {sourceWarehouseId ? (
                    <AsyncPaginate<
                      MaterialOption,
                      any,
                      { page: number },
                      false
                    >
                      key={`${sourceWarehouseId}-${materialsRefreshKey}`}
                      loadOptions={loadMaterialOptions}
                      additional={{ page: 1 }}
                      defaultOptions
                      loadOptionsOnMenuOpen
                      debounceTimeout={300}
                      value={selectedMaterial}
                      onChange={(option) => {
                        setSelectedMaterial(option);
                        setQuantity("1");
                        setSerialNumbers("");
                        setSenderRemark("");
                      }}
                      getOptionValue={(option) => option.value}
                      getOptionLabel={(option) => option.label}
                      formatOptionLabel={(option) => {
                        const displayUnit = getMaterialDisplayUnit(
                          option.material.unit,
                        );
                        const availableQuantity =
                          convertMaterialQuantity(
                            option.material.quantity,
                            option.material.unit,
                            displayUnit,
                          ) ?? option.material.quantity;
                        return (
                          <div className="py-1">
                            <p className="font-medium text-gray-900">
                              {option.material.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              SKU: {option.material.sku} · Available:{" "}
                              {availableQuantity.toLocaleString(undefined, {
                                maximumFractionDigits: 4,
                              })}{" "}
                              {displayUnit}
                            </p>
                          </div>
                        );
                      }}
                      placeholder="Search by material name or SKU..."
                      noOptionsMessage={() => "No available materials found"}
                      className="text-sm"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({ ...base, minHeight: 48 }),
                        menuPortal: (base) => ({ ...base, zIndex: 10010 }),
                      }}
                    />
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-500">
                      {isAdmin
                        ? "Select a source warehouse to load available materials."
                        : "No source warehouse is assigned to your account."}
                    </div>
                  )}
                  {selectedMaterial && (
                    <MaterialTransferItemEditor
                      material={selectedMaterial.material}
                      quantity={quantity}
                      onQuantityChange={setQuantity}
                      serialNumbers={serialNumbers}
                      onSerialNumbersChange={setSerialNumbers}
                      senderRemark={senderRemark}
                      onSenderRemarkChange={setSenderRemark}
                    />
                  )}
                  {selectedMaterial && (
                    <button
                      type="button"
                      onClick={addMaterial}
                      className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 sm:w-auto"
                    >
                      <Plus size={17} />
                      Add material
                    </button>
                  )}
                </div>

                {queuedMaterials.length > 0 && (
                  <div className="mt-5 overflow-hidden rounded-xl border border-blue-100 bg-blue-50">
                    <div className="flex items-center justify-between gap-3 border-b border-blue-200 bg-blue-100/50 p-3 sm:p-4">
                      <h3 className="text-sm font-semibold text-blue-900">
                        Materials to send ({queuedMaterials.length})
                      </h3>
                      <button
                        type="button"
                        onClick={() => setQueuedMaterials([])}
                        className="min-h-10 rounded-lg px-3 text-xs font-semibold text-red-600 hover:bg-white/70"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="grid gap-3 p-3 sm:p-4">
                      {queuedMaterials.map((item) => (
                        <article
                          key={item.material.materialId}
                          className="flex items-start justify-between gap-3 rounded-lg border border-blue-100 bg-white p-3 shadow-sm sm:p-4"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900">
                              {item.material.name}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              SKU: {item.material.sku}
                            </p>
                            <p className="mt-2 text-sm text-gray-700">
                              Quantity:{" "}
                              {item.displayQuantity.toLocaleString(undefined, {
                                maximumFractionDigits: 4,
                              })}{" "}
                              {item.displayUnit}
                            </p>
                            {item.serialNumbers.length > 0 && (
                              <p className="mt-1 break-words text-xs text-gray-500">
                                Serial numbers: {item.serialNumbers.join(", ")}
                              </p>
                            )}
                            {item.senderRemark && (
                              <p className="mt-1 break-words text-xs text-gray-500">
                                Remark: {item.senderRemark}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            aria-label={`Remove ${item.material.name}`}
                            onClick={() =>
                              setQueuedMaterials((items) =>
                                items.filter(
                                  (queued) =>
                                    queued.material.materialId !==
                                    item.material.materialId,
                                ),
                              )
                            }
                            className="flex min-h-10 min-w-10 shrink-0 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700"
                          >
                            <X size={17} />
                          </button>
                        </article>
                      ))}
                    </div>
                    <footer className="flex justify-end border-t border-blue-200 p-3 sm:p-4">
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="min-h-11 w-full rounded-lg bg-[#0077C0] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0069AA] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                      >
                        {loading ? "Creating transfer..." : "Create transfer"}
                      </button>
                    </footer>
                  </div>
                )}
              </div>
            </section>
          </div>,
          document.body,
        )}
    </>
  );
};

export default CreateMaterialTransferSection;
